import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, type ChipProps } from '@heroui/react';
import { LINE_STATUS_LABEL_KEYS } from '@/pages/catalogImport/notices';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { ImportRowStatus } from '../../importRows';

/** Each status's chip colour and variant; the row drawer's header shows the same chip. */
export const STATUS_CHIP: Record<
    ImportRowStatus,
    Pick<ChipProps, 'color' | 'variant'>
> = {
    ready: { color: 'success', variant: 'soft' },
    needsAttention: { color: 'danger', variant: 'soft' },
    excluded: { color: 'default', variant: 'tertiary' },
    unchecked: { color: 'default', variant: 'soft' },
    draft: { color: 'warning', variant: 'soft' },
};

/** Each status's label; the row drawer's header shows the same words. */
export const STATUS_LABEL_KEY = {
    ...LINE_STATUS_LABEL_KEYS,
    unchecked: 'catalogImport.status.unchecked',
} as const satisfies Record<ImportRowStatus, string>;

type StatusChipProps = {
    status: ImportRowStatus;
    rowNumber: number;
};

/**
 * A row's status: Ready (success), Needs attention (danger), Excluded
 * (muted), Not checked (neutral) for a row included again after a check
 * left it out, or Draft (warning) for a row a check with `importDrafts`
 * will import as a draft.
 */
export const StatusChip: React.FC<StatusChipProps> = ({
    status,
    rowNumber,
}) => {
    const { t } = useTranslation('pages');

    return (
        <Chip
            {...STATUS_CHIP[status]}
            data-status={status}
            size="sm"
            {...getNodeTestIdProp(`catalog-import-row-status-${rowNumber}`)}
        >
            {t(STATUS_LABEL_KEY[status])}
        </Chip>
    );
};
